"use client";
import { useUiControlLabels } from "@/components/i18n/ui-control-labels-context"


import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Mail, MessageSquare, Inbox, Settings, AlertTriangle, BarChart3, Play } from "lucide-react";
import { useRouter } from "next/navigation";
import type { EmailAgent } from "@/hooks/agents/email/use-agents";
import { AgentStatusBadge } from "./agent-status-badge";
import { AgentRoleBadge } from "@/components/agents/shared/agent-role-badge";
import { AGENT_PRIMARY_ROLE_USE_CASE } from "@/lib/ai/roles/types";
import { AgentIntegrationBadges } from "@/components/agents/shared/agent-integration-badges";
import type { AgentIntegrationStatus } from "@/lib/agents/shared/integration-status-types";
import { useTranslations } from "next-intl"



interface EmailAgentCardProps {
  agent: EmailAgent;
  onEdit: (agent: EmailAgent) => void;
  onTest?: (agent: EmailAgent) => void;
  canManage: boolean;
  canViewEmails: boolean;
  canViewAnalytics: boolean;
  pendingFeedbackCount?: number;
  integrationStatus?: AgentIntegrationStatus;
}

export function EmailAgentCard({
  agent,
  onEdit,
  onTest,
  canManage,
  canViewEmails,
  canViewAnalytics,
  pendingFeedbackCount,
  integrationStatus,
}: EmailAgentCardProps) {
  const {locale: uiFormattingLocale} = useUiControlLabels()


  const tUi = useTranslations("Email.AgentsEmailEmailAgentCard")

  const router = useRouter();

  const inboxes = agent.inboxes || [];
  const inboxCount = inboxes.length;

  // Check if any inbox has oauth issues
  const inboxesWithErrors = inboxes.filter(i => i.oauth_status && i.oauth_status !== 'connected');
  const hasOAuthErrors = inboxesWithErrors.length > 0;

  const getProviderBadge = (provider: string) => {
    switch (provider?.toLowerCase()) {
      case "gmail":
        return (
          <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-xs">
            <span key={"Gmail"}>{"Gmail"}</span></Badge>
        );
      case "outlook":
        return (
          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-xs">
            <span key={"Outlook"}>{"Outlook"}</span></Badge>
        );
      default:
        return (
          <Badge variant="outline" className="text-xs">
            {provider}
          </Badge>
        );
    }
  };

  return (
    <Card className="flex h-full flex-col transition-shadow hover:shadow-md">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="text-lg font-medium text-gray-900 mb-1">
              {agent.name}
            </CardTitle>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Inbox className="w-4 h-4" />
              <span key={inboxCount === 0
                  ? tUi("noInboxes")
                  : tUi("value0InboxesPlural", {count: inboxCount})}>
                {inboxCount === 0
                  ? tUi("noInboxes")
                  : tUi("value0InboxesPlural", {count: inboxCount})}
              </span>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <div className="flex items-center gap-2">
            {hasOAuthErrors && (
              <Badge
                variant="destructive"
                className="text-xs px-1.5 py-0.5 cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(agent);
                }}
                title={tUi("value0InboxEsNeedReconnection", { value0: String(inboxesWithErrors.length) })}
              >
                <AlertTriangle className="w-3 h-3 mr-1" />
                <span><span key={tUi("authError")}>{tUi("authError")}</span></span>
              </Badge>
            )}
            {pendingFeedbackCount && pendingFeedbackCount > 0 && (
              <Badge
                className="bg-orange-500 text-white text-xs px-1.5 py-0.5 cursor-pointer hover:bg-orange-600 transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  router.push(`/?page=qa-feedback&agentId=${agent.id}`);
                }}
                title={tUi("viewQAFeedback")}
              >
                <MessageSquare className="w-3 h-3 mr-1" />
                {pendingFeedbackCount}
              </Badge>
            )}
            <AgentStatusBadge status={agent.status} />
            </div>
            <AgentRoleBadge
              useCase={AGENT_PRIMARY_ROLE_USE_CASE.email_agent}
              roleSlugs={agent.config?.roles}
              roleLabel={agent.config?.role_label}
            />
            <AgentIntegrationBadges
              crmConnected={integrationStatus?.crmConnected ?? false}
              deliveryConnected={integrationStatus?.deliveryConnected ?? false}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col pt-0">
        <div className="flex h-full flex-1 flex-col space-y-3">
          {/* Inboxes list */}
          <div className="space-y-1">
            {inboxCount === 0 ? (
              <div className="flex items-center gap-2 text-sm">
                <Mail className="w-4 h-4 text-gray-400" />
                <span className="text-red-500 italic"><span key={tUi("noInboxConnected")}>{tUi("noInboxConnected")}</span></span>
              </div>
            ) : inboxCount <= 2 ? (
              // Show all inboxes if 2 or fewer
              inboxes.map((inbox) => (
                <div key={inbox.id} className="flex items-center gap-2 text-sm">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600 font-medium truncate flex-1">
                    {inbox.address}
                  </span>
                  {getProviderBadge(inbox.provider)}
                </div>
              ))
            ) : (
              // Show first 2 + "more" for 3+
              <>
                {inboxes.slice(0, 2).map((inbox) => (
                  <div key={inbox.id} className="flex items-center gap-2 text-sm">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600 font-medium truncate flex-1">
                      {inbox.address}
                    </span>
                    {getProviderBadge(inbox.provider)}
                  </div>
                ))}
                <div className="text-sm text-gray-500 pl-6">
                  <span>+</span>{inboxCount - 2} <span><span key={tUi("more")}>{tUi("more")}</span></span>
                </div>
              </>
            )}
          </div>

          {/* Auto-reply status */}
          {agent.config?.auto_reply?.enabled && (
            <div className="flex items-center gap-2 text-sm">
              <MessageSquare className="w-4 h-4 text-green-500" />
              <span className="text-green-600"><span key={tUi("autoReplyEnabled")}>{tUi("autoReplyEnabled")}</span></span>
            </div>
          )}

          {/* Dates */}
          <div className="text-xs text-gray-500 pt-2 border-t">
            <div>
              <span><span key={tUi("created")}>{tUi("created")}</span></span> {new Date(agent.created_at).toLocaleDateString(uiFormattingLocale)}
            </div>
          </div>

          <div className="mt-auto space-y-2 pt-5">
            {/* Primary Actions - View Emails + Analytics */}
            {(canViewEmails || canViewAnalytics) && (
              <div className="flex flex-wrap gap-2">
                {canViewEmails && (
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={(e) => {
                      e.stopPropagation();
                      router.push(`?page=agent-emails&agentId=${agent.id}`);
                    }}
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    <span><span key={tUi("viewEmails")}>{tUi("viewEmails")}</span></span>
                  </Button>
                )}
                {canViewAnalytics && (
                  <Button
                    variant="outline"
                    aria-label={tUi("analytics")}
                    onClick={(e) => {
                      e.stopPropagation();
                      router.push(`?page=agent-analytics&agentId=${agent.id}`);
                    }}
                  >
                    <BarChart3 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            )}

            {/* Secondary Actions */}
            {canManage && (
              <div className="flex gap-2">
                {onTest && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={(event) => {
                      event.stopPropagation();
                      onTest(agent);
                    }}
                  >
                    <Play className="w-4 h-4 mr-1" />
                    <span><span key={tUi("test")}>{tUi("test")}</span></span>
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => onEdit(agent)}
                >
                  <Settings className="w-4 h-4 mr-1" />
                  <span><span key={tUi("settings")}>{tUi("settings")}</span></span>
                </Button>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
